// Named, seeded random streams for the sim. One world seed; each system draws from its own stream
// (`rng.stream("combat")`, `rng.stream("ai")`), so adding a draw in one system never shifts another's
// sequence, and a replay or a restored save draws the same numbers. Never use Math.random in the sim.
//
// Generator: sfc32 (Chris Doty-Humphrey's Small Fast Counting generator; 128-bit state, a 32-bit counter
// that guarantees a period of at least 2^32, passes PractRand to terabytes). Each stream's state is seeded
// from FNV-1a(name) mixed with the world seed through splitmix32, then 12 discarded outputs. The state is
// four uint32s, so a save stores it as a plain array. The draw interface matches @cozy/procgen's `Rng`
// (next, range, int, pick, jitter) so agents meet one shape.

export type RngState = [number, number, number, number];

export interface Rng {
  /** [0, 1) */
  next(): number;
  /** [min, max) */
  range(min: number, max: number): number;
  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  pick<T>(items: readonly T[]): T;
  /** Symmetric jitter in [-amount, amount). */
  jitter(amount: number): number;
  /** True with probability p. */
  chance(p: number): boolean;
  /** Fisher–Yates, in place; returns the array. */
  shuffle<T>(items: T[]): T[];
  /** A raw uint32. */
  u32(): number;
  /** Snapshot of the state (for saves and replays). */
  state(): RngState;
  /** Restore a snapshot. */
  setState(s: RngState): void;
}

/** FNV-1a string hash → uint32 (same as @cozy/procgen's hashString). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function splitmix32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x9e3779b9) >>> 0;
    let z = a;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    return (z ^ (z >>> 15)) >>> 0;
  };
}

/** A standalone sfc32 generator from a 32-bit seed. */
export function sfc32(seed: number): Rng {
  const sm = splitmix32(seed);
  return fromState([sm(), sm(), sm(), sm()], 12);
}

function fromState(init: RngState, discard = 0): Rng {
  let a = init[0] >>> 0;
  let b = init[1] >>> 0;
  let c = init[2] >>> 0;
  let d = init[3] >>> 0;
  const u32 = (): number => {
    const t = (((a + b) >>> 0) + d) >>> 0;
    d = (d + 1) >>> 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) >>> 0;
    c = ((c << 21) | (c >>> 11)) >>> 0;
    c = (c + t) >>> 0;
    return t;
  };
  for (let i = 0; i < discard; i++) u32();
  const next = (): number => u32() / 4294967296;
  return {
    next,
    u32,
    range: (min, max) => min + (max - min) * next(),
    int: (min, max) => Math.floor(min + (max - min + 1) * next()),
    pick: (items) => {
      if (items.length === 0) throw new Error("pick from an empty list");
      return items[Math.floor(next() * items.length)]!;
    },
    jitter: (amount) => (next() * 2 - 1) * amount,
    chance: (p) => next() < p,
    shuffle: (items) => {
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        const t = items[i]!;
        items[i] = items[j]!;
        items[j] = t;
      }
      return items;
    },
    state: () => [a >>> 0, b >>> 0, c >>> 0, d >>> 0], // canonical uint32s: equal states snapshot (and hash) equal
    setState: (s) => {
      [a, b, c, d] = [s[0] >>> 0, s[1] >>> 0, s[2] >>> 0, s[3] >>> 0];
    },
  };
}

/** A world seed's named streams. */
export class RngStreams {
  readonly seed: number;
  private streams = new Map<string, Rng>();

  constructor(seed: number) {
    this.seed = seed >>> 0;
  }

  /** The stream with this name (created on first use; the same object after). */
  stream(name: string): Rng {
    let r = this.streams.get(name);
    if (!r) {
      r = this.fresh(name);
      this.streams.set(name, r);
    }
    return r;
  }

  /** Every stream's state, by name (save, replay checkpoint). */
  snapshot(): Record<string, RngState> {
    const out: Record<string, RngState> = {};
    for (const [k, r] of this.streams) out[k] = r.state();
    return out;
  }

  /** Restore a snapshot. A stream not in it had not been drawn from when it was taken, so it goes back to its seeded
   *  start (in place: objects already handed out stay valid). */
  restore(s: Record<string, RngState>): void {
    for (const [k, r] of this.streams) if (!(k in s)) r.setState(this.fresh(k).state());
    for (const [k, st] of Object.entries(s)) this.stream(k).setState(st);
  }

  private fresh(name: string): Rng {
    return sfc32((hashString(name) ^ Math.imul(this.seed, 0x9e3779b1)) >>> 0);
  }
}

export function createRng(seed: number): RngStreams {
  return new RngStreams(seed);
}
